import { useCallback, useEffect, useState, type CSSProperties, type ReactNode } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import { reviews, type Review } from "@/lib/reviews";
import { useAutoplay } from "@/lib/use-autoplay";
import tiktokShop from "@/images/marketplace/tiktok-shop.webp";
import tokopedia from "@/images/marketplace/tokopedia.webp";
import shopee from "@/images/marketplace/shopee.webp";

/**
 * Screenshots are drawn at a fixed fraction of their pixel size, so the
 * marketplace's text is the same size in every card (about 14px desktop,
 * 13px phone) however long the review. Cards are as wide as their review.
 */
const SCALE = 0.7;
const MOBILE_SCALE = 0.65;

/**
 * Buyer reviews, on Kanata green, directly under the hero. The headline is
 * Divine's (DEL-519), set exactly like the hero's title so the two read as
 * one pair (3 Nov), and the band is kept short so the moving cards show in
 * the first screen alongside the hero: Divine wants both in motion the moment
 * a visitor lands. The marketplaces are named by their logos, not in words:
 * TikTok Shop and Tokopedia share one pill because they are one shop now.
 *
 * The screenshots are the proof, so they are shown as they are rather than
 * retyped. Their widths vary a lot, so this is a centred, full-bleed strip of
 * cards of varying width rather than a fixed grid. Phones get the re-wrapped
 * `-m` screenshots (see lib/reviews.ts).
 */
export function Reviews() {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const autoplay = useAutoplay(5000);
  const { setApi: setAutoplayApi } = autoplay;
  // The counter and the autoplay both need the carousel.
  const onApi = useCallback(
    (a: CarouselApi) => {
      setApi(a);
      setAutoplayApi(a);
    },
    [setAutoplayApi],
  );

  useEffect(() => {
    if (!api) return;
    const sync = () => setCurrent(api.selectedScrollSnap());
    sync();
    api.on("select", sync).on("reInit", sync);
    return () => {
      api.off("select", sync).off("reInit", sync);
    };
  }, [api]);

  return (
    <section id="ulasan" className="overflow-hidden bg-brand pb-12 pt-6 text-white sm:pb-16 sm:pt-8">
      <div className="mx-auto max-w-6xl px-4 text-center sm:px-8">
        {/* The hero title's type, class for class (Hero.tsx). */}
        <h2 className="text-balance text-[1.75rem] font-medium leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
          Indonesia&apos;s #1 Standing Desk Brand
        </h2>
        <p className="mt-5 flex flex-wrap items-center justify-center gap-x-2 gap-y-2 text-base font-medium text-white/90 sm:text-lg">
          <span>Kata mereka dari halaman ulasan toko Kanata di</span>
          <span className="inline-flex items-center gap-2">
            <LogoPill>
              <MarketplaceLogo src={tiktokShop} alt="TikTok Shop" width={623} />
              <span aria-hidden="true" className="h-4 w-px bg-ink/15 sm:h-5" />
              <MarketplaceLogo src={tokopedia} alt="Tokopedia" width={544} />
            </LogoPill>
            <span>dan</span>
            <LogoPill>
              <MarketplaceLogo src={shopee} alt="Shopee" width={350} />
            </LogoPill>
          </span>
        </p>
      </div>

      <Carousel
        setApi={onApi}
        opts={{ align: "center", loop: true }}
        plugins={autoplay.plugins}
        className="mt-5 sm:mt-6"
        aria-label="Ulasan pembeli"
      >
        <CarouselContent className="items-start">
          {reviews.map((review) => (
            <CarouselItem key={review.image.src} className="basis-auto">
              <ReviewCard review={review} />
            </CarouselItem>
          ))}
        </CarouselContent>

        <div className="mt-8 flex items-center justify-center gap-4">
          <CarouselPrevious
            variant="white"
            className="static size-10 translate-x-0 translate-y-0 text-brand-dark"
          />
          <span className="min-w-14 text-center text-sm tabular-nums text-white/80" aria-live="polite">
            {current + 1} / {reviews.length}
          </span>
          <CarouselNext
            variant="white"
            className="static size-10 translate-x-0 translate-y-0 text-brand-dark"
          />
        </div>
      </Carousel>
    </section>
  );
}

function ReviewCard({ review }: { review: Review }) {
  const width = {
    "--w": `${Math.round(review.mobile.width * MOBILE_SCALE)}px`,
    "--w-md": `${Math.round(review.image.width * SCALE)}px`,
  } as CSSProperties;

  return (
    <figure
      style={width}
      className="max-w-[86vw] rounded-[1.75rem] bg-white p-5 md:rounded-[2rem] md:p-7"
    >
      <picture>
        <source
          media="(max-width: 767px)"
          srcSet={review.mobile.src}
          width={review.mobile.width}
          height={review.mobile.height}
        />
        <img
          src={review.image.src}
          alt={`Ulasan bintang 5 dari ${review.name}: “${review.text}”`}
          width={review.image.width}
          height={review.image.height}
          loading="lazy"
          className="h-auto w-[var(--w)] max-w-full md:w-[var(--w-md)]"
        />
      </picture>
    </figure>
  );
}

/**
 * A white pill behind the logos. The files themselves are transparent, but
 * Tokopedia's green and TikTok's black are hard to read on Kanata green.
 */
function LogoPill({ children }: { children: ReactNode }) {
  return <span className="inline-flex items-center gap-2.5 rounded-full bg-white px-3 py-1.5 sm:px-4">{children}</span>;
}

/** A marketplace logo, drawn at the pill's height. All the files are 112px tall. */
function MarketplaceLogo({ src, alt, width }: { src: string; alt: string; width: number }) {
  return <img src={src} alt={alt} width={width} height={112} className="h-4 w-auto sm:h-5" />;
}
